import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { isTMA, retrieveLaunchParams } from '@telegram-apps/sdk-react'

import { isDevEnv } from '@/shared/lib/utils'
import { TelegramPlatformEnum } from '@/enums/telegram-platform-enum'

import {
  createHashHistory,
  createRouter,
  RouterProvider,
} from '@tanstack/react-router'
import NotFound from '@/routes/not-found'

import './telegram-mock'
import { routeTree } from './routeTree.gen'
import { init } from './init'
import { base64UrlDecode } from '@/shared/lib/base64url'
import i18n from './i18n'

const container = document.getElementById('root')
const root = createRoot(container!)

const debug = import.meta.env.VITE_DEBUG === 'true'

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.reportingObserverIntegration(),
    Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }),
  ],
  tracesSampleRate: 0.05,
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 0.0,
})

try {
  let platform: TelegramPlatformEnum = TelegramPlatformEnum.Web

  /*
   * Assign platform and debug flags from launch params, otherwise use defaults
   * (for example, if the app is launched from the browser)
   *
   * */
  if (isTMA()) {
    const launchParams = retrieveLaunchParams()

    if (launchParams.tgWebAppData?.user) {
      const { user } = launchParams.tgWebAppData

      Sentry.setUser({ id: user.id, username: user.username })
    }

    Sentry.setContext('telegram_webapp', {
      platform: launchParams.tgWebAppPlatform,
      version: launchParams.tgWebAppVersion,
    })

    platform = launchParams.tgWebAppPlatform as TelegramPlatformEnum
  }
  if (
    isDevEnv() &&
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    import.meta.env.VITE_ENABLE_MSW === '1'
  ) {
    const { worker } = await import('./mock/browser')
    await worker.start({
      serviceWorker: { url: '/mockServiceWorker.js' },
      onUnhandledRequest: 'bypass',
    })
  }

  await init({
    debug,
    eruda:
      debug &&
      ![TelegramPlatformEnum.Web, TelegramPlatformEnum.WebA].includes(platform),
    mockForMacOS: platform === TelegramPlatformEnum.MacOS,
    skipFullscreen: ![
      TelegramPlatformEnum.iOS,
      TelegramPlatformEnum.Android,
    ].includes(platform),
  }).then(async () => {
    console.info('🎉 Application initialized successfully')

    // Change the route and handle referral codes
    const launchParams = retrieveLaunchParams()
    const userLanguage = launchParams.tgWebAppData?.user?.language_code
    // Устанавливаем русский язык если язык пользователя русский, иначе английский
    if (userLanguage === 'ru') {
      await i18n.changeLanguage('ru')
      console.info('🌐 Language set to Russian')
    } else {
      await i18n.changeLanguage('en')
      console.info('🌐 Language set to English')
    }

    // Обрабатываем реферальный код и маршрутизацию
    const path = launchParams.tgWebAppData?.start_param
      ? base64UrlDecode(launchParams.tgWebAppData.start_param)
      : ''

    // Проверяем, есть ли реферальный код в декодированном пути
    if (path.includes('/?ref=')) {
      // Убираем слеш в начале
      const cleanPath = path.substring(1)
      const urlParams = new URLSearchParams(cleanPath.split('?')[1])
      const referralCode = urlParams.get('ref')

      if (referralCode) {
        // Передаем реферальный код синхронно в auth
        const { processReferralCode } = await import('@/shared/auth')
        processReferralCode(referralCode)
      }
    }

    if (!window.location.hash.startsWith('#/')) {
      const url = new URL(window.location.href)
      const hash = '#/'
      const urlPath = window.location.pathname
      const newUrl = `${url.origin}${urlPath}${hash}`
      const normalizedPath = path.startsWith('/') ? path.substring(1) : path

      window.history.replaceState(null, '', `${newUrl}${normalizedPath}`)
    }

    const router = createRouter({
      routeTree,
      history: createHashHistory(),
      defaultNotFoundComponent: NotFound,
      scrollRestoration: true,
    })

    root.render(
      import.meta.env.VITE_REACT_STRICT_MODE ? (
        <StrictMode>
          <RouterProvider router={router} />
        </StrictMode>
      ) : (
        <RouterProvider router={router} />
      ),
    )
  })
} catch (e: unknown) {
  console.warn('Unsupported telegram client version:', e)

  // todo: create a component for this view (should be designed)
  root.render(
    <>
      <span>You are using too old Telegram client to run this application</span>
      {debug && (
        <div className={'flex flex-col gap-2'}>
          <div>
            <span>Launch params:</span>
            <pre>{JSON.stringify(retrieveLaunchParams(), null, 2)}</pre>
          </div>

          <div>
            <span>Error: {e instanceof Error ? e.message : String(e)}</span>
            <pre>{JSON.stringify(e, null, 2)}</pre>
          </div>
        </div>
      )}
    </>,
  )
}
